/* =============================================================================
   plate.js - renders the plate preview as an SVG string.
   Pure: no DOM access, no event listeners, no state. main.js wires it up.
   Requires config.js and validate.js and must be loaded after them.

   Coordinate system: the viewBox is the plate size in millimetres, so every
   value below is a real millimetre value and the SVG scales losslessly via
   CSS (width: 100%). All geometry comes from PlateConfig.METRICS.
   ========================================================================== */
var PlatePreview = (function (cfg, val) {
  'use strict';

  var M = cfg.METRICS;
  var COLORS = cfg.COLORS;

  // TODO: replace with FE-Schrift (fälschungserschwerende Schrift) once the
  // licence is cleared - it is not free for commercial use in every
  // distribution. Until then this is a wide bold sans placeholder; textLength
  // below pins the real width so swapping the font cannot break the layout.
  var FONT_STACK = "'Arial Narrow', 'Helvetica Neue', Arial, sans-serif";

  /* --- small helpers ------------------------------------------------------- */

  // Trim floating point noise; SVG does not need more than 3 decimals of a mm.
  function n(value) {
    return String(Math.round(value * 1000) / 1000);
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* --- plate body ---------------------------------------------------------- */

  // Fill only, no outline of its own: the embossed frame is the edge of the
  // plate, so nothing may be visible outside it.
  function plateBody(w, h) {
    return '<rect x="0" y="0" width="' + n(w) + '" height="' + n(h) + '"' +
      ' rx="' + n(M.cornerRadius) + '" ry="' + n(M.cornerRadius) + '"' +
      ' fill="' + COLORS.plate + '"/>';
  }

  // The embossed black frame runs around the whole plate, including across the
  // EU band, so it is drawn last - the blue field sits underneath it and must
  // never cover it. With borderInset = 0 the outer edge of the stroke lands
  // exactly on the plate outline, so nothing shows beyond the frame.
  function embossedBorder(w, h) {
    var inset = M.borderInset + M.borderWidth / 2;
    var radius = Math.max(0, M.cornerRadius - inset);
    return '<rect x="' + n(inset) + '" y="' + n(inset) +
      '" width="' + n(w - 2 * inset) + '" height="' + n(h - 2 * inset) + '"' +
      ' rx="' + n(radius) + '" ry="' + n(radius) + '"' +
      ' fill="none" stroke="' + COLORS.border + '" stroke-width="' + n(M.borderWidth) + '"/>';
  }

  /* --- EU band ------------------------------------------------------------- */

  // Five-pointed star, upright, as the EU flag draws it.
  function star(cx, cy, outerRadius) {
    var innerRadius = outerRadius * 0.382; // golden ratio, as in the EU spec
    var points = [];
    for (var i = 0; i < 10; i++) {
      var radius = (i % 2 === 0) ? outerRadius : innerRadius;
      var angle = (Math.PI / 5) * i - Math.PI / 2;
      points.push(n(cx + radius * Math.cos(angle)) + ',' + n(cy + radius * Math.sin(angle)));
    }
    return '<polygon points="' + points.join(' ') + '" fill="' + COLORS.star + '"/>';
  }

  // Blue band on the left, reaching the plate edge and rounded on its two left
  // corners so it follows the plate outline instead of poking out of it. The
  // embossed frame is painted over it afterwards.
  // Breite des blauen Feldes. Das Parkplatzschild traegt ein breiteres, dessen
  // Mass sich aus der Plattenhoehe ergibt - die Vorlage zeichnet es als SVG im
  // festen Seitenverhaeltnis 47.5 : 99.4.
  function bandWidth(size) {
    return size && size.parking ? size.h * M.parkingBandRatio : M.euroWidth;
  }

  function euroBandShape(h, w) {
    var r = M.cornerRadius;
    w = w || M.euroWidth;
    return '<path d="M ' + n(r) + ' 0 H ' + n(w) + ' V ' + n(h) +
      ' H ' + n(r) + ' A ' + n(r) + ' ' + n(r) + ' 0 0 1 0 ' + n(h - r) +
      ' V ' + n(r) + ' A ' + n(r) + ' ' + n(r) + ' 0 0 1 ' + n(r) + ' 0 Z"' +
      ' fill="' + COLORS.euro + '"/>';
  }

  function euroBand(h) {
    var out = [euroBandShape(h)];
    var cx = M.euroWidth / 2;
    var cy = h * M.starsCenterRatio;

    for (var i = 0; i < 12; i++) {
      var angle = (Math.PI / 6) * i - Math.PI / 2; // 12 stars, one every 30 degrees
      out.push(star(
        cx + M.starCircleRadius * Math.cos(angle),
        cy + M.starCircleRadius * Math.sin(angle),
        M.starRadius
      ));
    }

    out.push('<text x="' + n(cx) + '" y="' + n(h * M.euroLetterRatio) + '"' +
      ' font-family="' + FONT_STACK + '" font-weight="700"' +
      ' font-size="' + n(M.euroLetterHeight / M.capHeightRatio) + '"' +
      ' fill="' + COLORS.euroText + '" text-anchor="middle" dominant-baseline="central">D</text>');

    return out.join('');
  }

  // Parkplatzschild: dasselbe blaue Feld, aber mit einem weissen P statt des
  // Sternenkranzes und des D. Das P sitzt mittig und ist gut die halbe
  // Plattenhoehe hoch, wie im p.svg der Vorlage.
  function parkingBand(h, w) {
    var capHeight = h * M.parkingLetterRatio;
    return euroBandShape(h, w) +
      '<text x="' + n(w / 2) + '" y="' + n(h / 2) + '"' +
      ' font-family="' + FONT_STACK + '" font-weight="700"' +
      ' font-size="' + n(capHeight / M.capHeightRatio) + '"' +
      ' fill="' + COLORS.euroText + '" text-anchor="middle" dominant-baseline="central">P</text>';
  }

  /* --- line items ----------------------------------------------------------
     Every item reports its natural width and draws itself with its left edge
     at x and its vertical centre at y = 0. The whole line is translated and
     scaled afterwards, so items never need to know where they end up.        */

  // Letters and digits have different advance widths, and both are given for
  // METRICS.charRefHeight, so they scale with the character height of the size.
  function charWidthAt(character, capHeight) {
    var base = /[0-9]/.test(character) ? M.digitWidth : M.charWidth;
    return base * (capHeight / M.charRefHeight);
  }

  function textItem(value, capHeight) {
    var gap = M.charGap * (capHeight / M.charRefHeight);
    var width = 0;
    for (var i = 0; i < value.length; i++) {
      if (i > 0) { width += gap; }
      width += charWidthAt(value.charAt(i), capHeight);
    }
    return {
      w: width,
      h: capHeight,
      isText: true,
      draw: function (x) {
        // textLength pins the width to the millimetre value we calculated, so
        // the layout holds no matter which fallback font the browser picks.
        return '<text x="' + n(x) + '" y="0"' +
          ' font-family="' + FONT_STACK + '" font-weight="700"' +
          ' font-size="' + n(capHeight / M.capHeightRatio) + '"' +
          ' letter-spacing="' + n(gap) + '"' +
          ' textLength="' + n(width) + '" lengthAdjust="spacingAndGlyphs"' +
          ' fill="' + COLORS.text + '" text-anchor="start" dominant-baseline="central"' +
          ' xml:space="preserve">' + esc(value) + '</text>';
      }
    };
  }

  // The Siegel is stamped onto the plate by the Zulassungsstelle after purchase.
  // We never print it - the seal drawn here is a preview of the finished plate
  // and a spacing reservation, nothing that ends up on the delivered product.

  // Grey dashed circle, used only where there is nothing better to draw:
  // an unrecognised city code, or a state that has no icon file yet. It is
  // never drawn behind an icon - the icons carry their own shape.
  function sealPlaceholder(cx, cy, radius) {
    return '<circle cx="' + n(cx) + '" cy="' + n(cy) + '" r="' + n(radius) + '"' +
      ' fill="' + COLORS.sealFill + '" stroke="' + COLORS.sealOutline + '"' +
      ' stroke-width="' + n(M.sealStrokeWidth) + '"' +
      ' stroke-dasharray="' + M.sealDashArray + '"' +
      ' class="kfz-seal-placeholder"/>';
  }

  // The state icons are finished Siegel badges: they already carry their own
  // ring, field and lettering. So the seal is the icon, drawn at the full
  // reserved diameter with nothing added around or behind it. Anything we drew
  // ourselves - rings, a disc, a second name arc - only doubled what the
  // artwork already says.
  // ownRow: the seal is alone on its row and sits in the middle of it. The
  // offset exists only to drop the stamp below the centre line of a row of
  // characters, which is not what happens there.
  function sealItem(iconUrl, hasInput, ownRow) {
    var r = (M.sealDiameter * M.sealIconRatio) / 2;
    var cy = ownRow ? 0 : M.sealOffsetY;
    return {
      w: M.sealDiameter,
      // Vertical extent around the centre line: the badge is inset by
      // sealIconRatio and then shifted, so this is what it actually occupies.
      // Reserving the full diameter plus the offset would be too pessimistic
      // and would shrink narrow plates for no reason.
      h: 2 * (Math.abs(cy) + r),
      draw: function (x) {
        var cx = x + M.sealDiameter / 2;
        var size = M.sealDiameter * M.sealIconRatio;

        // Nothing typed yet: show an empty plate rather than a lone grey
        // circle floating on it.
        if (!hasInput) { return ''; }

        // No icon for this state yet, or an unrecognised code: the seal area
        // stays a plain reservation.
        if (!iconUrl) { return sealPlaceholder(cx, cy, r); }

        return '<image href="' + esc(iconUrl) + '" xlink:href="' + esc(iconUrl) + '"' +
          ' x="' + n(cx - size / 2) + '" y="' + n(cy - size / 2) + '"' +
          ' width="' + n(size) + '" height="' + n(size) + '"' +
          ' preserveAspectRatio="xMidYMid meet"' +
          ' class="kfz-seal-icon"/>';
      }
    };
  }

  // Vertical oval with the two season months stacked, e.g. 03 above 10.
  function seasonItem(from, to) {
    var rx = M.seasonWidth / 2;
    var ry = M.seasonHeight / 2;
    var fontSize = M.seasonTextHeight / M.capHeightRatio;
    var offset = M.seasonHeight / 4;
    return {
      w: M.seasonWidth,
      h: M.seasonHeight,
      draw: function (x) {
        var cx = x + rx;
        function month(value, y) {
          return '<text x="' + n(cx) + '" y="' + n(y) + '"' +
            ' font-family="' + FONT_STACK + '" font-weight="700"' +
            ' font-size="' + n(fontSize) + '" fill="' + COLORS.text + '"' +
            ' text-anchor="middle" dominant-baseline="central">' + esc(value) + '</text>';
        }
        return '<ellipse cx="' + n(cx) + '" cy="0" rx="' + n(rx) + '" ry="' + n(ry) + '"' +
          ' fill="none" stroke="' + COLORS.text + '"' +
          ' stroke-width="' + n(M.seasonStrokeWidth) + '"/>' +
          month(from, -offset) + month(to, offset);
      }
    };
  }

  /* --- line composition ---------------------------------------------------- */

  // Digits and the E / H suffix form one run: the suffix follows the digits
  // directly, without a gap.
  function digitRun(state, type) {
    if (!state.digits) { return ''; }
    return state.digits + (type.suffix || '');
  }

  function hasSeason(state, type) {
    return type.isSeason && val.isMonth(state.seasonFrom) && val.isMonth(state.seasonTo);
  }

  // Returns an array of lines, each line an array of items. Partial input just
  // yields fewer items - the seal is always reserved, so an empty plate still
  // looks like a plate.
  function buildLines(state, type, size, capHeight, seal) {
    var twoLine = size.twoLine;

    // Parkplatzschild: eine einzige freie Zeile, kein Siegel, kein Suffix und
    // keine Saison - auf einem Parkplatzschild gibt es davon nichts.
    if (size.parking) {
      var text = state.parkingText || '';
      return text ? [[textItem(text, capHeight)]] : [[]];
    }

    var digits = digitRun(state, type);
    var season = hasSeason(state, type) ? seasonItem(state.seasonFrom, state.seasonTo) : null;

    // Motorcycle plates give the seal a row of its own, centred under the city
    // code, with the number below it. The plate is nearly square, so a third
    // row fits. The type's sealAfterDigits does not apply here - on these
    // plates the seal always sits in the middle row, E and H included.
    if (size.sealBelowCity) {
      var cityRow = [];
      var numberRow = [];
      if (state.cityCode) { cityRow.push(textItem(state.cityCode, capHeight)); }
      if (state.letters) { numberRow.push(textItem(state.letters, capHeight)); }
      if (digits) { numberRow.push(textItem(digits, capHeight)); }
      if (season) { numberRow.push(season); }
      return [cityRow, [seal], numberRow];
    }

    var head = [];
    var tail = [];

    if (state.cityCode) { head.push(textItem(state.cityCode, capHeight)); }
    if (!type.sealAfterDigits) { head.push(seal); }
    if (state.letters) { (twoLine ? tail : head).push(textItem(state.letters, capHeight)); }

    if (digits) { tail.push(textItem(digits, capHeight)); }
    if (type.sealAfterDigits) { tail.push(seal); }
    if (season) { tail.push(season); }

    return twoLine ? [head, tail] : [head.concat(tail)];
  }

  function lineWidth(items) {
    var width = 0;
    for (var i = 0; i < items.length; i++) {
      width += items[i].w;
      if (i > 0) { width += M.groupGap; }
    }
    return width;
  }

  function drawLine(items) {
    var out = [];
    var x = 0;
    for (var i = 0; i < items.length; i++) {
      if (i > 0) { x += M.groupGap; }
      out.push(items[i].draw(x));
      x += items[i].w;
    }
    return out.join('');
  }

  // Tallest item on a line: the season oval is taller than the characters.
  function lineHeight(items, capHeight) {
    var height = capHeight;
    for (var i = 0; i < items.length; i++) {
      height = Math.max(height, items[i].h);
    }
    return height;
  }

  // Same, but without the character floor: a row holding nothing but the seal
  // is as tall as the seal, not as tall as a row of characters. Only the sizes
  // that give the seal a row of its own measure this way.
  function rowHeight(items, capHeight) {
    var height = 0;
    var hasText = false;
    for (var i = 0; i < items.length; i++) {
      height = Math.max(height, items[i].h);
      if (items[i].isText) { hasText = true; }
    }
    return hasText ? Math.max(height, capHeight) : height;
  }

  /* --- layout ---------------------------------------------------------------
     Computes everything positional in one place, so renderPlate() only paints
     and measure() can report the same numbers without duplicating the maths.  */
  function computeLayout(rawOrState) {
    var state = val.normalize(rawOrState);
    var size = cfg.getSize(state.sizeId) || cfg.SIZES[0];
    var type = cfg.getType(state.type) || cfg.TYPES[0];
    var capHeight = size.charHeight;

    // A recognised city code gives the seal its district name and state icon.
    // An unrecognised one leaves both empty, and the seal stays the placeholder.
    var found = val.lookupDistrict(state.cityCode);
    var hasInput = !!(state.cityCode || state.letters || state.digits);
    var seal = sealItem(
      (found && found.state) ? cfg.assetUrl(found.state.iconUrl) : '',
      hasInput,
      !!size.sealBelowCity
    );

    var lines = buildLines(state, type, size, capHeight, seal);

    var band = bandWidth(size);
    var availableW = size.w - band - 2 * M.contentPadding;
    var availableH = size.h - 2 * M.contentPadding;

    var widest = 0;
    var tallestLine = 0;
    var i;
    for (i = 0; i < lines.length; i++) {
      widest = Math.max(widest, lineWidth(lines[i]));
      tallestLine = Math.max(tallestLine, lineHeight(lines[i], capHeight));
    }
    // Row heights. The classic plate gives every row the height of the tallest
    // one, so the rows of characters sit symmetrically - that is what the car
    // sizes have always done and it stays exactly as it was. A plate with the
    // seal on its own row measures every row for itself instead: a row holding
    // nothing but the seal must not claim the height of a row of characters,
    // or three rows would not fit on a 200 mm plate.
    var rowHeights = [];
    for (i = 0; i < lines.length; i++) {
      rowHeights.push(size.sealBelowCity ? rowHeight(lines[i], capHeight) : tallestLine);
    }
    var naturalH = 0;
    for (i = 0; i < rowHeights.length; i++) {
      if (i > 0) { naturalH += M.lineGap; }
      naturalH += rowHeights[i];
    }

    // Safety net only: with a correct charHeight per size this stays at 1 for
    // normal combinations. It fires for the longest ones, the way a real plate
    // falls back to a narrower font.
    var scale = 1;
    if (widest > 0) { scale = Math.min(scale, availableW / widest); }
    if (naturalH > 0) { scale = Math.min(scale, availableH / naturalH); }

    var top = (size.h - naturalH * scale) / 2;

    // Rows are stacked from the top of the block downwards. An empty row draws
    // nothing but still takes its space, so a half-filled plate keeps the rows
    // it already has where they will end up.
    var placed = [];
    var rowTop = 0;
    for (i = 0; i < lines.length; i++) {
      if (lines[i].length) {
        placed.push({
          items: lines[i],
          naturalWidth: lineWidth(lines[i]),
          x: band + M.contentPadding + (availableW - lineWidth(lines[i]) * scale) / 2,
          centerY: top + (rowTop + rowHeights[i] / 2) * scale
        });
      }
      rowTop += rowHeights[i] + M.lineGap;
    }

    return {
      state: state, size: size, type: type,
      capHeight: capHeight, scale: scale,
      availableWidth: availableW, widestLine: widest,
      lines: placed
    };
  }

  /* --- public API ----------------------------------------------------------
     renderPlate(state) -> SVG string. Accepts raw or normalized input and
     never throws on partial input.                                           */
  function renderReference(layout) {
    var state = layout.state;
    var district = val.lookupDistrict(state.cityCode);
    var sealUrl = district && district.state ? cfg.assetUrl(district.state.iconUrl) : '';
    var seal = state.cityCode && sealUrl ? '<img class="kfz-reference-seal" src="' + esc(sealUrl) + '" alt="" width="20" height="20">' : '';
    var band = '<svg class="kfz-reference-euro" viewBox="0 0 44 90" aria-hidden="true"><rect width="44" height="90" fill="#003399"/>';
    for (var i = 0; i < 12; i++) {
      var angle = Math.PI * i / 6 - Math.PI / 2;
      band += star(22 + 14 * Math.cos(angle), 27 + 14 * Math.sin(angle), 2.7);
    }
    band += '<text x="22" y="77" font-family="Arial,sans-serif" font-size="23" font-weight="700" text-anchor="middle" fill="#fff">D</text></svg>';
    return '<div class="kfz-reference-plate' + (layout.size.twoLine ? ' kfz-reference-plate--two' : '') + '" role="img" aria-label="' + esc(val.formatKennzeichen(state) || 'Kennzeichen-Vorschau') + '">' + band +
      '<div class="kfz-reference-number"><span>' + esc(state.cityCode) + '</span>' + seal +
      (layout.size.twoLine ? '<span class="kfz-reference-break"></span>' : '') +
      '<span>' + esc(state.letters) + '</span><span>' + esc(state.digits) + '</span></div></div>';
  }

  function renderPlate(rawOrState) {
    var layout = computeLayout(rawOrState);
    if (!layout.size.parking && layout.type.id === 'standard' &&
        [520, 480, 460, 420, 400, 340, 320, 280].indexOf(layout.size.w) !== -1) {
      return renderReference(layout);
    }
    var w = layout.size.w;
    var h = layout.size.h;

    // Paint order matters: body, then the blue band, then the frame on top of
    // both, then the characters.
    var band = bandWidth(layout.size);
    var out = [plateBody(w, h),
      layout.size.parking ? parkingBand(h, band) : euroBand(h),
      embossedBorder(w, h)];

    for (var i = 0; i < layout.lines.length; i++) {
      var line = layout.lines[i];
      out.push('<g transform="translate(' + n(line.x) + ' ' + n(line.centerY) + ')' +
        ' scale(' + n(layout.scale) + ')">' + drawLine(line.items) + '</g>');
    }

    var label = val.formatKennzeichen(layout.state) || 'Kennzeichen-Vorschau';

    return '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"' +
      ' viewBox="0 0 ' + n(w) + ' ' + n(h) + '"' +
      ' width="100%" role="img" aria-label="' + esc(label) + '"' +
      ' style="display:block;height:auto">' + out.join('') + '</svg>';
  }

  // Diagnostics for the geometry check sheet: how much the content had to be
  // scaled to fit. scale < 1 means the plate cannot hold this combination at
  // the configured character height.
  function measure(rawOrState) {
    var layout = computeLayout(rawOrState);
    return {
      sizeId: layout.size.id,
      twoLine: layout.size.twoLine,
      charHeight: layout.capHeight,
      scale: layout.scale,
      widestLine: layout.widestLine,
      availableWidth: layout.availableWidth
    };
  }

  return {
    renderPlate: renderPlate,
    measure: measure
  };
})(PlateConfig, PlateValidate);

